import { ReactComponent as Vector } from 'assets/icons/insights-bg.svg';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import {
  AddPostBanner,
  AddQuietlyBanner,
  ArticleCreationMethods,
  ParseFileActions,
  WriteWithAIActionButtons,
  WriteWithAIFormValues,
  WriteWithAIOnSelectOptions,
} from '@buildwise/libs/posts';
import WeWillWriteActions from '@buildwise/libs/posts/components/WeWillWrite/WeWillWriteActions';
import { ModeratedContentData, PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import {
  ArticleSourceType,
  ArticleType,
  AttachmentType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { BUILDWISE_MODULES_FEATURES } from 'components/drawer/constants';
import { theme } from 'config/theme';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import useUserData, { UserType } from 'hooks/useUserData';
import { Dispatch, SetStateAction, useCallback, useMemo, useState, ChangeEvent } from 'react';
import { FormProvider, useFormContext, UseFormReturn, useWatch } from 'react-hook-form';
import NotificationOffFillIcon from 'remixicon-react/NotificationOffFillIcon';
import GroupLineIcon from 'remixicon-react/GroupLineIcon';
import InformationLineIcon from 'remixicon-react/InformationLineIcon';
import genAIKnowledgeAPI from 'api/GenAIKnowledgeAPI';
import { extractPlainText } from 'utility/helpers';

type UsersSearchResponseType = {
  totalElements?: number;
  totalNumber?: number;
  totalPages?: number;
  userResponse: UserType[];
};

type PostFormDialogActionsProps = {
  article: ArticleType | undefined;
  articleCreationMethod: ArticleCreationMethods;
  articleType: ArticleSourceType;
  canPublishPost: boolean;
  createPlaylist: boolean;
  forward: boolean;
  isCreateDraft: boolean;
  isEditing: boolean;
  isOnePostSelected: boolean;
  isPublishingFileAsOnePost: boolean;
  isSavingDraft: boolean;
  loadingFiles: boolean;
  response: any;
  startAnotherPost: boolean;
  writeWithAIFormState: UseFormReturn<WriteWithAIFormValues>;
  downloadedFiles: AttachmentType[];
  actingUser: UserType | null;
  isFetchingModeratedContent?: boolean;
  isAddingQuietly: boolean;
  setIsFetchingModeratedContent?: Dispatch<React.SetStateAction<boolean>>;
  onForward: () => void;
  onGenerate: () => void;
  onParseFile: (options: { isOnePostSelected: boolean }) => Promise<void>;
  onSaveDraft: () => void;
  onSelect: (options: WriteWithAIOnSelectOptions) => void;
  onSubmit: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setPublishingFileAsOnePost: Dispatch<SetStateAction<boolean>>;
  setResponse: any;
  setStartAnotherPost: Dispatch<React.SetStateAction<boolean>>;
  setOpenContentModerationModal: Dispatch<React.SetStateAction<boolean>>;
  setModeratedContent: React.Dispatch<React.SetStateAction<ModeratedContentData>>;
  setOpenNovaAdminConfirmation: Dispatch<React.SetStateAction<boolean>>;
};

function PostFormDialogActions({
  article,
  articleCreationMethod,
  articleType,
  canPublishPost,
  createPlaylist,
  forward,
  isCreateDraft,
  isSavingDraft,
  isEditing,
  isOnePostSelected,
  isPublishingFileAsOnePost,
  loadingFiles,
  response,
  writeWithAIFormState,
  downloadedFiles,
  actingUser,
  isFetchingModeratedContent,
  isAddingQuietly,
  onForward,
  onGenerate,
  onParseFile,
  onSaveDraft,
  onSelect,
  onSubmit,
  setOpen,
  setPublishingFileAsOnePost,
  setOpenContentModerationModal,
  setModeratedContent,
  setIsFetchingModeratedContent,
  setResponse,
  setOpenNovaAdminConfirmation,
}: PostFormDialogActionsProps) {
  const {
    control,
    formState: { isSubmitting },
    setValue,
    getValues,
    trigger,
    getFieldState,
  } = useFormContext<PostFormData>();
  const projectSpecific = useWatch({
    control,
    name: 'projectSpecific',
    defaultValue: true,
  });
  const isWritingWithAI = useWatch({ control, name: 'isWritingWithAI', defaultValue: false });
  const articleTypeFormValue = useWatch({ control, name: 'articleType' });
  const { homeModuleFeatures, singularProjectsModuleName } = useBuildWiseModules();
  const { user, company, isNovaAdmin } = useUserData();

  // NOTE : It is used to show the notified users for Questions
  // const departments: string[] = useWatch({ control, name: 'targetGroups', defaultValue: [] });
  // const regions: string[] = useWatch({ control, name: 'region', defaultValue: [] });
  // const [openNotifiedUsers, setOpenNotifiedUsers] = useState(false);
  // const { data: peopleData } = useSearchItemQuery<UsersSearchResponseType>(
  //   {
  //     searchType: 'users',
  //     operator: 'or',
  //     filterBy: {
  //       filters: [
  //         {
  //           filterType: 'name',
  //           filterValue: 'ALL',
  //         },
  //       ],
  //     },
  //   },
  //   articleType === ArticleSourceType.QUESTION,
  // );
  // const users = useMemo(() => {
  //   if (!(departments?.length || regions?.length)) return [];

  //   const filterByCriteria = (_user: UserType) => {
  //     const matchesDepartments = departments.some((value) => _user?.department?.includes(value));
  //     const matchesRegions = regions.some((value) => _user?.region?.includes(value));

  //     return (
  //       ((departments.length && matchesDepartments) || (regions.length && matchesRegions)) &&
  //       (!departments.length || !regions.length || (matchesDepartments && matchesRegions))
  //     );
  //   };

  //   return (peopleData?.userResponse ?? []).filter(filterByCriteria);
  // }, [departments, regions, peopleData?.userResponse]);

  const { singularCollectionsModuleName } = useBuildWiseModules();

  const handleCheckboxChange = useCallback((_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setValue('projectSpecific', !checked, { shouldDirty: true, shouldValidate: true });
  }, []);

  const handleSubmit = useCallback(async () => {
    const shouldOpenNovaAdminCorformationModal =
      isNovaAdmin && user?.username !== article?.createdBy;
    if (company?.settings?.contentModeration) {
      const isManualPost =
        [ArticleSourceType.POST, ArticleSourceType.NOTES].includes(articleType) &&
        articleCreationMethod === ArticleCreationMethods.WRITE_MANUALLY;

      const isValid = await trigger();
      if (!isManualPost) {
        if (shouldOpenNovaAdminCorformationModal && isValid) {
          setOpenNovaAdminConfirmation(true);
        } else {
          onSubmit();
        }
        return;
      }

      if (!isValid) {
        // custom logic to scroll to the first error field
        const allFieldNames = Object.keys(getValues());

        const firstErrorField = allFieldNames?.find((field) => {
          const state = getFieldState(field);
          return state?.error;
        });

        if (firstErrorField) {
          const errorElement = document?.getElementById(firstErrorField);

          if (errorElement instanceof HTMLElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorElement.focus?.();
          }
        }

        return;
      }

      setIsFetchingModeratedContent?.(true);

      const { title, description } = getValues();

      const payload = {
        title,
        description,
      };

      try {
        const formData = new FormData();
        formData.append('content_moderation_request_str', JSON.stringify(payload));

        const { data } = await genAIKnowledgeAPI.checkContentModeration(formData);

        setIsFetchingModeratedContent?.(false);

        if (
          data?.ai_generated_response?.needs_moderation &&
          data?.ai_generated_response?.modifications?.length
        ) {
          setModeratedContent(data);
          setOpenContentModerationModal(true);
          return;
        } else if (shouldOpenNovaAdminCorformationModal) {
          setOpenNovaAdminConfirmation(true);
          return;
        } else {
          onSubmit();
        }
      } catch (error) {
        console.log('CONTENT_MODERATION_ERROR', error);
      } finally {
        setIsFetchingModeratedContent?.(false);
      }
    } else if (shouldOpenNovaAdminCorformationModal) {
      setOpenNovaAdminConfirmation(true);
    } else {
      onSubmit();
    }
  }, [
    isNovaAdmin,
    user,
    company?.settings?.contentModeration,
    article,
    articleType,
    articleCreationMethod,
    onSubmit,
    setIsFetchingModeratedContent,
    setOpenNovaAdminConfirmation,
    setOpenContentModerationModal,
    setModeratedContent,
    getValues,
  ]);

  const handlePost = useCallback(() => {
    setValue('articleType', articleType, { shouldDirty: true, shouldValidate: true });
    handleSubmit();
  }, []);

  const handleAddQuietly = useCallback(() => {
    setValue('articleType', ArticleSourceType.NOTES, { shouldDirty: true, shouldValidate: true });
    handleSubmit();
  }, [actingUser]);

  return (
    <>
      {forward ? (
        <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%">
          <Button disabled={isSubmitting || loadingFiles} onClick={onForward} variant="contained">
            {isSubmitting ? (
              <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
            ) : (
              'Forward'
            )}
          </Button>
        </Box>
      ) : (
        <>
          {(articleCreationMethod === ArticleCreationMethods.WRITE_MANUALLY ||
            articleType === ArticleSourceType.LESSON ||
            (articleCreationMethod !== ArticleCreationMethods.PARSE_FILE && isWritingWithAI) ||
            isPublishingFileAsOnePost) && (
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" ml={-1}>
                {isEditing && article?.isDraft && (
                  <Button
                    disabled={isSubmitting || loadingFiles || isFetchingModeratedContent}
                    onClick={onSaveDraft}
                  >
                    {isSubmitting && isSavingDraft ? (
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.black })}
                        size={20}
                      />
                    ) : (
                      'Update Draft'
                    )}
                  </Button>
                )}
                {!isEditing && !isCreateDraft && (
                  <Button
                    disabled={isSubmitting || loadingFiles || isFetchingModeratedContent}
                    onClick={onSaveDraft}
                  >
                    {isSubmitting && isSavingDraft ? (
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.black })}
                        size={20}
                      />
                    ) : (
                      'Save as Draft'
                    )}
                  </Button>
                )}
                {articleType === ArticleSourceType.LESSON && (
                  <FormControlLabel
                    control={<Checkbox onChange={handleCheckboxChange} disabled={isSubmitting} />}
                    label="Show on Feed"
                    checked={!projectSpecific}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                {isCreateDraft ? (
                  <Button
                    disabled={isSubmitting || loadingFiles || isFetchingModeratedContent}
                    onClick={onSaveDraft}
                    variant="contained"
                  >
                    {isSubmitting ? (
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.black })}
                        size={20}
                      />
                    ) : (
                      'Save Draft'
                    )}
                  </Button>
                ) : !isCreateDraft && isEditing ? (
                  (!article?.isDraft || canPublishPost) && (
                    <>
                      <Button
                        disabled={isSubmitting || loadingFiles || isFetchingModeratedContent}
                        onClick={handleSubmit}
                        variant="contained"
                      >
                        {isSubmitting && !isSavingDraft ? (
                          <CircularProgress
                            sx={(theme) => ({ color: theme.palette.custom.black })}
                            size={20}
                          />
                        ) : article?.isDraft ? (
                          articleType === ArticleSourceType.QUESTION ||
                          articleType === ArticleSourceType.SUGGESTION ||
                          articleType === ArticleSourceType.LESSON ? (
                            'Submit'
                          ) : article.originIndicator === 'PLAYLIST' ? (
                            `Create ${singularCollectionsModuleName}`
                          ) : (
                            'Post'
                          )
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </>
                  )
                ) : (
                  <>
                    {/* {!createPlaylist &&
                      articleCreationMethod === ArticleCreationMethods.WRITE_MANUALLY &&
                      articleType !== ArticleSourceType.QUESTION &&
                      articleType !== ArticleSourceType.SUGGESTION && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mr: 2,
                          }}
                        >
                          <Checkbox
                            onChange={(event) => {
                              setStartAnotherPost(event.target.checked);
                            }}
                            checked={startAnotherPost}
                          />
                          <Typography>Start another post</Typography>
                        </Box>
                      )} */}
                    {/* {articleType === ArticleSourceType.QUESTION && (
                      <>
                        <Tooltip
                          placement="top"
                          title={
                            <Typography fontSize={14}>
                              {users.length} members will be notified about this question
                            </Typography>
                          }
                        >
                          <Box sx={{ display: 'flex', mt: 0.5, cursor: 'default' }}>
                            <GroupLineIcon size={18} />
                            <Typography
                              sx={(theme) => ({
                                ml: 0.5,
                                color: theme.palette.custom.mistBlue,
                                fontSize: 14,
                              })}
                            >
                              {users.length}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </>
                    )} */}
                    {canPublishPost && (
                      <>
                        {!isEditing &&
                          articleCreationMethod === ArticleCreationMethods.WRITE_MANUALLY &&
                          [ArticleSourceType.POST, ArticleSourceType.NOTES].includes(articleType) &&
                          homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.NOTES]?.isActive &&
                          (!homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.NOTES]?.roles
                            ?.length ||
                            user?.roles.some((role) =>
                              homeModuleFeatures[
                                BUILDWISE_MODULES_FEATURES.HOME.NOTES
                              ].roles?.includes(role.roleName),
                            )) && (
                            <Button
                              variant="outlined"
                              disabled={isSubmitting || loadingFiles || isFetchingModeratedContent}
                              onClick={isAddingQuietly ? handlePost : handleAddQuietly}
                              endIcon={
                                !isSubmitting &&
                                (isAddingQuietly ? <AddPostBanner /> : <AddQuietlyBanner />)
                              }
                              sx={(theme) => ({
                                backgroundColor: theme.palette.custom.white,
                              })}
                            >
                              {(isSubmitting || isFetchingModeratedContent) &&
                              !isSavingDraft &&
                              (isAddingQuietly
                                ? articleTypeFormValue === ArticleSourceType.POST
                                : articleTypeFormValue === ArticleSourceType.NOTES) ? (
                                <CircularProgress
                                  sx={(theme) => ({ color: theme.palette.custom.black })}
                                  size={20}
                                />
                              ) : isAddingQuietly ? (
                                'Add Post'
                              ) : (
                                'Add Quietly'
                              )}
                            </Button>
                          )}
                        <Button
                          disabled={isSubmitting || loadingFiles || isFetchingModeratedContent}
                          onClick={isAddingQuietly ? handleAddQuietly : handlePost}
                          variant="contained"
                        >
                          {(isSubmitting || isFetchingModeratedContent) &&
                          !isSavingDraft &&
                          (isAddingQuietly
                            ? articleTypeFormValue === ArticleSourceType.NOTES
                            : articleTypeFormValue === articleType) ? (
                            <CircularProgress
                              sx={(theme) => ({ color: theme.palette.custom.black })}
                              size={20}
                            />
                          ) : createPlaylist ? (
                            `Create ${singularCollectionsModuleName}`
                          ) : articleType === ArticleSourceType.QUESTION ||
                            articleType === ArticleSourceType.SUGGESTION ||
                            articleType === ArticleSourceType.LESSON ||
                            articleType === ArticleSourceType.NOTES ? (
                            'Submit'
                          ) : isAddingQuietly ? (
                            'Add Quietly'
                          ) : (
                            'Add Post'
                          )}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}
          {articleCreationMethod === ArticleCreationMethods.WRITE_WITH_AI && !isWritingWithAI && (
            <FormProvider {...writeWithAIFormState}>
              <WriteWithAIActionButtons onGenerate={onGenerate} onSelect={onSelect} />
            </FormProvider>
          )}
          {articleCreationMethod === ArticleCreationMethods.PARSE_FILE &&
            !isPublishingFileAsOnePost && (
              <ParseFileActions
                downloadedFiles={downloadedFiles}
                setResponse={setResponse}
                response={response}
                onParseFile={onParseFile}
                setOpen={setOpen}
                isOnePostSelected={isOnePostSelected}
                setPublishingFileAsOnePost={setPublishingFileAsOnePost}
              />
            )}
          {articleCreationMethod === ArticleCreationMethods.WE_WILL_WRITE && (
            <WeWillWriteActions setOpen={setOpen} onParseFile={onParseFile} response={response} />
          )}
        </>
      )}

      {/* {openNotifiedUsers && (
        <NotifiedUsersModal open={openNotifiedUsers} users={users} setOpen={setOpenNotifiedUsers} />
      )} */}
    </>
  );
}

export default PostFormDialogActions;
